/* =======================================================
   Theme JS — variant picker, qty stepper, AJAX cart drawer
   ======================================================= */
(function () {
  'use strict';

  const money = (cents) => {
    const value = (cents / 100).toFixed(2);
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.shopCurrency || 'USD',
    }).format(parseFloat(value));
  };

  /* ------------------------------------------------- Cart drawer */
  const drawer = document.querySelector('[data-cart-drawer]');
  const drawerBody = document.querySelector('[data-cart-body]');
  const cartCount = document.querySelector('[data-cart-count]');

  function openDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer();
      refreshCart();
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeDrawer();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });

  async function refreshCart() {
    try {
      const res = await fetch('/?section_id=cart-drawer-fragment', { credentials: 'same-origin' });
      // We use the simpler /cart.js + re-render approach instead.
    } catch (err) { /* no-op */ }
    try {
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      updateCartCount(cart.item_count);
      renderDrawer(cart);
    } catch (err) {
      console.error('Cart refresh failed', err);
    }
  }

  function updateCartCount(count) {
    if (!cartCount) return;
    cartCount.textContent = count;
    cartCount.style.display = count > 0 ? '' : 'none';
  }

  function renderDrawer(cart) {
    if (!drawerBody) return;
    if (cart.item_count === 0) {
      drawerBody.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Your cart is empty.</p>
          <a href="/" class="btn btn--primary">Shop now</a>
        </div>`;
      return;
    }
    const lines = cart.items.map((item) => `
      <li class="cart-line" data-line-key="${item.key}">
        <a href="${item.url}" class="cart-line__media">
          ${item.image ? `<img src="${item.image}" alt="" loading="lazy">` : ''}
        </a>
        <div class="cart-line__details">
          <a href="${item.url}" class="cart-line__title">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-line__variant">${escapeHtml(item.variant_title)}</p>` : ''}
          <div class="cart-line__qty">
            <button type="button" class="qty-stepper__btn" data-cart-decrease data-line-key="${item.key}" aria-label="Decrease">−</button>
            <span class="cart-line__qty-value">${item.quantity}</span>
            <button type="button" class="qty-stepper__btn" data-cart-increase data-line-key="${item.key}" aria-label="Increase">+</button>
            <button type="button" class="cart-line__remove" data-cart-remove data-line-key="${item.key}">Remove</button>
          </div>
        </div>
        <div class="cart-line__price">${money(item.final_line_price)}</div>
      </li>`).join('');

    drawerBody.innerHTML = `
      <ul class="cart-drawer__items">${lines}</ul>
      <footer class="cart-drawer__footer">
        <p class="cart-drawer__subtotal">
          <span>Subtotal</span>
          <span>${money(cart.total_price)}</span>
        </p>
        <p class="cart-drawer__note">Shipping and taxes calculated at checkout.</p>
        <a href="/cart" class="btn btn--secondary btn--full">View cart</a>
        <form action="/cart" method="post">
          <button type="submit" name="checkout" class="btn btn--primary btn--full">Check out</button>
        </form>
      </footer>`;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ---- AJAX add-to-cart from product forms */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const button = form.querySelector('[data-add-to-cart]');
    const originalText = button ? button.innerHTML : '';
    if (button) {
      button.disabled = true;
      button.innerHTML = '<span>Adding…</span>';
    }
    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add item');
      }
      await refreshCart();
      openDrawer();
    } catch (err) {
      alert(err.message || 'Sorry, that item could not be added.');
    } finally {
      if (button) {
        button.disabled = false;
        button.innerHTML = originalText;
      }
    }
  });

  /* ---- Cart drawer line updates */
  document.addEventListener('click', async (e) => {
    const inc = e.target.closest('[data-cart-increase]');
    const dec = e.target.closest('[data-cart-decrease]');
    const rm = e.target.closest('[data-cart-remove]');
    if (!inc && !dec && !rm) return;
    e.preventDefault();
    const key = (inc || dec || rm).dataset.lineKey;
    if (!key) return;
    let quantityChange = 0;
    if (inc) quantityChange = +1;
    if (dec) quantityChange = -1;
    if (rm) quantityChange = -999;
    try {
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      const line = cart.items.find((i) => i.key === key);
      if (!line) return;
      const newQty = rm ? 0 : Math.max(0, line.quantity + quantityChange);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: newQty }),
      });
      await refreshCart();
    } catch (err) {
      console.error('Cart update failed', err);
    }
  });

  /* ------------------------------------------------- Variant picker */
  document.querySelectorAll('[data-section-type="hero-product"]').forEach((section) => {
    const variantSelect = section.querySelector('[data-variant-select]');
    if (!variantSelect) return;
    const optionInputs = section.querySelectorAll('.variant-picker__input');
    const priceEl = section.querySelector('[data-price]');
    const comparePriceEl = section.querySelector('[data-compare-price]');
    const buttonPriceEl = section.querySelector('[data-button-price]');
    const buttonText = section.querySelector('[data-add-to-cart-text]');
    const button = section.querySelector('[data-add-to-cart]');
    const valueLabels = section.querySelectorAll('[data-selected-value]');

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const fieldsets = section.querySelectorAll('.variant-picker');
        const selected = [];
        fieldsets.forEach((fs, i) => {
          const checked = fs.querySelector('input:checked');
          selected[i] = checked ? checked.value : null;
          const label = fs.querySelector('[data-selected-value]');
          if (label && checked) label.textContent = checked.value;
        });
        // Find matching option in the select
        const target = Array.from(variantSelect.options).find((opt) => {
          return opt.textContent.trim().split(' / ').every((v, i) => {
            return selected[i] === undefined || v === selected[i];
          });
        });
        if (target) {
          variantSelect.value = target.value;
          const price = target.dataset.price;
          const comparePrice = target.dataset.comparePrice;
          const available = target.dataset.available === 'true';
          if (price && priceEl) priceEl.textContent = price;
          if (buttonPriceEl && price) buttonPriceEl.textContent = price;
          if (comparePriceEl) {
            if (comparePrice && comparePrice !== price && comparePrice !== '$0.00') {
              comparePriceEl.textContent = comparePrice;
              comparePriceEl.style.display = '';
            } else {
              comparePriceEl.style.display = 'none';
            }
          }
          if (button) {
            button.disabled = !available;
            if (buttonText) {
              if (available) {
                buttonText.innerHTML = `Add to cart — <span data-button-price>${price}</span>`;
              } else {
                buttonText.textContent = 'Sold out';
              }
            }
          }
        }
      });
    });
  });

  /* ------------------------------------------------- Quantity stepper */
  document.addEventListener('click', (e) => {
    const inc = e.target.closest('[data-qty-increase]');
    const dec = e.target.closest('[data-qty-decrease]');
    if (!inc && !dec) return;
    const wrapper = e.target.closest('.qty-stepper');
    const input = wrapper && wrapper.querySelector('[data-qty-input]');
    if (!input) return;
    let val = parseInt(input.value, 10) || 1;
    if (inc) val += 1;
    if (dec) val = Math.max(1, val - 1);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------- Init */
  document.addEventListener('DOMContentLoaded', () => {
    if (cartCount) updateCartCount(parseInt(cartCount.textContent, 10) || 0);
  });
})();
